// ✅ src/pages/Asset/AssetMasterListing.jsx
// (FULL FILE - MOVABLE/IMMOVABLE TABLE + ASPX RADIO BEHAVIOR + API INTEGRATED + UPDATE FLOW + LOCALSTORAGE HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSearch, FaPlus, FaEdit } from "react-icons/fa";
import AssetLayoutWrapper from "../../components/AssetLayoutWrapper";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/assetMasterListing";

const AssetMasterListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ get values from localStorage
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid") || "";
  const branchType = getLS("branchType") || getLS("branchtype") || "";
  const userId = getLS("userId") || getLS("loginuserid") || "";

  // ✅ common headers for backend
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ✅ Radio like ASPX: Movable / ImMovable / Scrap
  const [assetTypeRadio, setAssetTypeRadio] = useState("Movable");

  // ✅ Backend mode mapping:
  // Movable   -> Others
  // ImMovable -> Land
  // Scrap     -> Scrap
  // Vehicle   -> Vehicle
  const mode = useMemo(() => {
    if (assetTypeRadio === "ImMovable") return "Land";
    if (assetTypeRadio === "Scrap") return "Scrap";
    if (assetTypeRadio === "Vehicle") return "Vehicle";
    return "Others";
  }, [assetTypeRadio]);

  const [formData, setFormData] = useState({
    assetTypeId: "",
    assetSubTypeId: "",
    makeId: "",
    modelId: "",
    purchaseFromId: "",
    locationId: "",
    locationText: "",
    idCodeNo: "",
    assetCode: "",
    surveyNo: "",
    pattaNo: "",
    taxTypeId: "",
    fromYear: "",
    toYear: "",
  });

  const [assetTypes, setAssetTypes] = useState([]);
  const [assetSubTypes, setAssetSubTypes] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [purchaseFrom, setPurchaseFrom] = useState([]);
  const [locations, setLocations] = useState([]);
  const [taxTypes, setTaxTypes] = useState([]);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [scrapIds, setScrapIds] = useState(new Set());

  const [docOpen, setDocOpen] = useState(false);
  const [docTitle, setDocTitle] = useState("");
  const [docRows, setDocRows] = useState([]);
  const [docLoading, setDocLoading] = useState(false);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;
  const requiredClass = "text-red-500";

  const normalizeDD = (arr) =>
    (arr || []).map((x) => ({
      id: x.id ?? x.value ?? x.Value ?? "",
      name: x.name ?? x.text ?? x.Text ?? "",
    }));

  const isMovable = assetTypeRadio === "Movable";
  const isImMovable = assetTypeRadio === "ImMovable";
  const isScrap = assetTypeRadio === "Scrap";
  const isVehicle = assetTypeRadio === "Vehicle";

  const enableOthersFilters = isMovable || isScrap || isVehicle;
  const enableLandFilters = isImMovable;
  const enableAssetSubType = isMovable || isVehicle;

  const onRadioChange = (nextRadio) => {
    setAssetTypeRadio(nextRadio);

    setFormData({
      assetTypeId: "",
      assetSubTypeId: "",
      makeId: "",
      modelId: "",
      purchaseFromId: "",
      locationId: "",
      locationText: "",
      idCodeNo: "",
      assetCode: "",
      surveyNo: "",
      pattaNo: "",
      taxTypeId: "",
      fromYear: "",
      toYear: "",
    });

    setRecords([]);
    setSelectedIds(new Set());
    setScrapIds(new Set());
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "assetTypeId") {
      setFormData((p) => ({
        ...p,
        assetTypeId: value,
        assetSubTypeId: "",
      }));
      return;
    }

    if (name === "locationId") {
      const selected = locations.find((x) => String(x.id) === String(value));
      setFormData((p) => ({
        ...p,
        locationId: value,
        locationText: selected?.name || "",
      }));
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleAddNew = () => {
    navigate("/Asset/Assetmaster", {
      state: { mode: assetTypeRadio, id: "" },
    });
    toast.info("Opening new asset master form...");
  };

  const getAssetId = (row) =>
    row?.assetId ??
    row?.fld_Assetid ??
    row?.fld_assetid ??
    row?.AssetID ??
    row?.assetid ??
    row?.id ??
    "";

  const handleUpdate = (row) => {
    const assetId = getAssetId(row);
    if (!assetId) return toast.error("Asset id not found in row");

    navigate(
      `/Asset/Assetmaster?mode=${encodeURIComponent(
        assetTypeRadio
      )}&id=${encodeURIComponent(assetId)}`,
      {
        state: { mode: assetTypeRadio, id: assetId },
      }
    );
  };

  // ✅ Load dropdowns using localStorage headers
  const loadDropdowns = async () => {
    try {
      const type = mode === "Land" ? "L" : "%";

      const [atRes, makeRes, modelRes, pfRes, locRes, taxRes] =
        await Promise.all([
          axios.get(`${API_BASE}/assetTypes`, {
            params: { type },
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/makes`, {
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/models`, {
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/purchaseFrom`, {
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/locations`, {
            headers: commonHeaders,
          }),
          axios.get(`${API_BASE}/taxTypes`, {
            headers: commonHeaders,
          }),
        ]);

      setAssetTypes(normalizeDD(atRes.data?.data));
      setMakes(normalizeDD(makeRes.data?.data));
      setModels(normalizeDD(modelRes.data?.data));
      setPurchaseFrom(normalizeDD(pfRes.data?.data));
      setLocations(normalizeDD(locRes.data?.data));
      setTaxTypes(normalizeDD(taxRes.data?.data));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load dropdowns");
    }
  };

  const loadSubTypes = async (assetTypeId) => {
    if (!assetTypeId) {
      setAssetSubTypes([]);
      return;
    }

    try {
      const res = await axios.get(`${API_BASE}/assetSubTypes`, {
        params: { assetTypeId },
        headers: commonHeaders,
      });
      setAssetSubTypes(normalizeDD(res.data?.data));
    } catch (err) {
      setAssetSubTypes([]);
    }
  };

  useEffect(() => {
    loadDropdowns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, commonHeaders]);

  useEffect(() => {
    if (!enableAssetSubType) {
      setAssetSubTypes([]);
      return;
    }

    if (formData.assetTypeId) loadSubTypes(formData.assetTypeId);
    else setAssetSubTypes([]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.assetTypeId, enableAssetSubType]);

  const handleSearch = async () => {
    try {
      setLoading(true);

      const params =
        mode === "Land"
          ? {
              mode: "Land",
              assetTypeId: formData.assetTypeId || "Select",
              taxTypeId: formData.taxTypeId || "Select",
              fromYear: formData.fromYear || "",
              toYear: formData.toYear || "",
              locationId: formData.locationId || "Select",
              assetCode: formData.assetCode || "",
              pattaNo: formData.pattaNo || "",
              surveyNo: formData.surveyNo || "",
            }
          : mode === "Scrap"
          ? {
              mode: "Scrap",
              assetTypeId: formData.assetTypeId || "Select",
              makeId: formData.makeId || "Select",
              modelId: formData.modelId || "Select",
              purchaseFromId: formData.purchaseFromId || "Select",
              locationText: formData.locationText || "Select",
              idCodeNo: formData.idCodeNo || "",
            }
          : mode === "Vehicle"
          ? {
              mode: "Vehicle",
              assetTypeId: formData.assetTypeId || "Select",
              assetSubTypeId: formData.assetSubTypeId || "Select",
              makeId: formData.makeId || "Select",
              modelId: formData.modelId || "Select",
              purchaseFromId: formData.purchaseFromId || "Select",
              locationText: formData.locationText || "Select",
              idCodeNo: formData.idCodeNo || "",
            }
          : {
              mode: "Others",
              assetTypeId: formData.assetTypeId || "Select",
              assetSubTypeId: formData.assetSubTypeId || "Select",
              makeId: formData.makeId || "Select",
              modelId: formData.modelId || "Select",
              purchaseFromId: formData.purchaseFromId || "Select",
              locationText: formData.locationText || "Select",
              idCodeNo: formData.idCodeNo || "",
            };

      const res = await axios.get(`${API_BASE}/list`, {
        params,
        headers: commonHeaders,
      });

      const data = res.data?.data || [];
      setRecords(data);
      setSelectedIds(new Set());
      setScrapIds(new Set());

      if (data.length === 0) toast.info("No Records Found");
      else toast.success("Loaded records");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load records");
      setRecords([]);
      setSelectedIds(new Set());
      setScrapIds(new Set());
    } finally {
      setLoading(false);
    }
  };

  const openDocs = async (type, record) => {
    try {
      const assetId = getAssetId(record);
      if (!assetId) {
        toast.error("Asset id not found in row");
        return;
      }

      setDocOpen(true);
      setDocTitle(`${type} - AssetID: ${assetId}`);
      setDocRows([]);
      setDocLoading(true);

      if (mode !== "Land") {
        const res = await axios.get(`${API_BASE}/documents/invoice/${assetId}`, {
          headers: commonHeaders,
        });
        setDocRows(res.data?.data || []);
      } else {
        const doctype = type === "Tax Document" ? "tax" : "land";
        const res = await axios.get(`${API_BASE}/documents/asset-det/${assetId}`, {
          params: { doctype },
          headers: commonHeaders,
        });
        setDocRows(res.data?.data || []);
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to load documents");
      setDocRows([]);
    } finally {
      setDocLoading(false);
    }
  };

  const closeDocs = () => {
    setDocOpen(false);
    setDocTitle("");
    setDocRows([]);
  };

  const toggleSelected = (assetId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const toggleScrap = (assetId) => {
    setScrapIds((prev) => {
      const next = new Set(prev);
      if (next.has(assetId)) next.delete(assetId);
      else next.add(assetId);
      return next;
    });
  };

  const handleRemoveSelected = () => {
    if (selectedIds.size === 0) return toast.info("Select rows to remove");
    toast.info(
      `Remove clicked for AssetIds: ${Array.from(selectedIds).join(", ")}`
    );
  };

  const handleConfirmScrap = () => {
    if (scrapIds.size === 0) return toast.info("Select rows to scrap");
    toast.info(`Scrap confirm clicked for AssetIds: ${Array.from(scrapIds).join(", ")}`);
  };

  const movableColumns = useMemo(
    () => [
      { key: "sl", label: "Sl No" },
      { key: "assetTypeName", label: "Asset Type Name" },
      { key: "assetSubTypeName", label: "Asset Sub Type" },
      { key: "regNo", label: "RegNo" },
      { key: "idCodeNo", label: "ID code No" },
      { key: "makeName", label: "Make Name" },
      { key: "modelName", label: "Model Name" },
      { key: "purchaseDate", label: "Purchase Date" },
      { key: "purchaseValue", label: "Purchase Value" },
      { key: "location", label: "Location" },
      { key: "invoiceDoc", label: "Invoice Document" },
      { key: "update", label: "Update" },
      { key: "select", label: "Select" },
      { key: "scrap", label: "Scrap" },
    ],
    []
  );

  const immovableColumns = useMemo(
    () => [
      { key: "sl", label: "Sl No" },
      { key: "assetTypeName", label: "Asset Type Name" },
      { key: "assetCode", label: "Asset Code" },
      { key: "location", label: "Location" },
      { key: "pattaNo", label: "Patta Number" },
      { key: "surveyNo", label: "Survey Number" },
      { key: "landDoc", label: "Land Document" },
      { key: "taxDoc", label: "Tax Document" },
      { key: "update", label: "Update" },
    ],
    []
  );

  const columns = useMemo(() => {
    if (mode === "Land") return immovableColumns;
    return movableColumns;
  }, [mode, immovableColumns, movableColumns]);

  const pick = (row, keys) => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== undefined && v !== null && String(v) !== "NaN") return v;
    }
    return "";
  };

  const getRowValue = (row, key) => {
    if (
      key === "sl" ||
      key === "invoiceDoc" ||
      key === "select" ||
      key === "scrap" ||
      key === "landDoc" ||
      key === "taxDoc" ||
      key === "update"
    )
      return "";

    const map = {
      assetTypeName: [
        "assetTypeName",
        "fld_assettypename",
        "fld_AssetTypeName",
        "AssetTypeName",
      ],
      location: ["location", "fld_BranchName", "fld_branchname", "Location"],
      assetSubTypeName: [
        "assetSubTypeName",
        "fld_assetsubtypename",
        "fld_AssetSubTypeName",
        "AssetSubTypeName",
      ],
      regNo: ["regNo", "fld_regno", "fld_RegNo", "RegNo", "description"],
      idCodeNo: [
        "idCodeNo",
        "fld_IDCodeno",
        "fld_idcodeno",
        "fld_IDCodeNo",
        "IDCodeNo",
        "fld_IDCode",
      ],
      makeName: ["makeName", "fld_makename", "fld_MakeName", "MakeName"],
      modelName: ["modelName", "fld_modelname", "fld_ModelName", "ModelName"],
      purchaseDate: [
        "purchaseDate",
        "fld_purchasedate",
        "fld_PurchaseDate",
        "PurchaseDate",
      ],
      purchaseValue: [
        "purchaseValue",
        "fld_purchasevalue",
        "fld_PurchaseValue",
        "PurchaseValue",
      ],
      assetCode: ["assetCode", "fld_assetcode", "fld_AssetCode", "AssetCode"],
      pattaNo: [
        "pattaNo",
        "fld_pattano",
        "fld_PattaNo",
        "PattaNo",
        "pattaNumber",
      ],
      surveyNo: [
        "surveyNo",
        "fld_surveyno",
        "fld_SurveyNo",
        "SurveyNo",
        "surveyNumber",
      ],
    };

    return pick(row, map[key] || [key]);
  };

  const rowClass = (idx) => {
    if (mode !== "Land") return "bg-purple-600 text-white";
    return idx % 2 === 0
      ? isDark
        ? "bg-gray-800"
        : "bg-gray-50"
      : isDark
      ? "bg-gray-900"
      : "bg-white";
  };

  return (
    <AssetLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">ASSET MASTER LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-8">
            <h1 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              ASSET MASTER LISTING
            </h1>

            <button
              onClick={handleAddNew}
              className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
            >
              <FaPlus />
              Add New
            </button>
          </div>

          <div
            className={`rounded-xl border mb-6 p-6 ${
              isDark
                ? "border-gray-700 bg-gray-800/50"
                : "border-gray-200 bg-gray-50"
            }`}
          >
            <div className="mb-6">
              <div className="flex items-center gap-6">
                <span className={labelClass}>Asset Type</span>

                {["Movable", "ImMovable", "Scrap", "Vehicle"].map((r) => (                  <label key={r} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="assetTypeRadio"
                      checked={assetTypeRadio === r}
                      onChange={() => onRadioChange(r)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                      {r}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-4">
                <div>
                  <label className={labelClass}>
                    AssetType <span className={requiredClass}>*</span>
                  </label>
                  <select
                    name="assetTypeId"
                    value={formData.assetTypeId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {assetTypes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>AssetSubType</label>
                  <select
                    name="assetSubTypeId"
                    value={formData.assetSubTypeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableAssetSubType}
                  >
                    <option value="">Select</option>
                    {assetSubTypes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Make</label>
                  <select
                    name="makeId"
                    value={formData.makeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableOthersFilters}
                  >
                    <option value="">Select</option>
                    {makes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>ID Code No</label>
                  <input
                    type="text"
                    name="idCodeNo"
                    value={formData.idCodeNo}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableOthersFilters}
                  />
                </div>

                <div>
                  <label className={labelClass}>Survey No</label>
                  <input
                    type="text"
                    name="surveyNo"
                    value={formData.surveyNo}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  />
                </div>

                <div>
                  <label className={labelClass}>Asset Code</label>
                  <input
                    type="text"
                    name="assetCode"
                    value={formData.assetCode}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  />
                </div>

                <div>
                  <label className={labelClass}>From Year</label>
                  <input
                    type="text"
                    name="fromYear"
                    value={formData.fromYear}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className={labelClass}>Purchased From</label>
                  <select
                    name="purchaseFromId"
                    value={formData.purchaseFromId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableOthersFilters}
                  >
                    <option value="">Select</option>
                    {purchaseFrom.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Model/HP/Capacity</label>
                  <select
                    name="modelId"
                    value={formData.modelId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableOthersFilters}
                  >
                    <option value="">Select</option>
                    {models.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Location</label>
                  <select
                    name="locationId"
                    value={formData.locationId}
                    onChange={handleChange}
                    className={inputClass}
                  >
                    <option value="">Select</option>
                    {locations.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Patta No</label>
                  <input
                    type="text"
                    name="pattaNo"
                    value={formData.pattaNo}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  />
                </div>

                <div>
                  <label className={labelClass}>Tax Type</label>
                  <select
                    name="taxTypeId"
                    value={formData.taxTypeId}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  >
                    <option value="">Select</option>
                    {taxTypes.map((x) => (
                      <option key={x.id} value={x.id}>
                        {x.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>To Year</label>
                  <input
                    type="text"
                    name="toYear"
                    value={formData.toYear}
                    onChange={handleChange}
                    className={inputClass}
                    disabled={!enableLandFilters}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={handleSearch}
                disabled={loading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-10 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl ${
                  loading ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                <FaSearch />
                {loading ? "Loading..." : "Search"}
              </button>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    {columns.map((c, i) => (
                      <th
                        key={c.key}
                        className={`px-3 py-3 text-center font-semibold ${
                          i !== columns.length - 1 ? "border-r border-white/20" : ""
                        }`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {records.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length}
                        className={`px-4 py-6 text-center ${
                          isDark ? "text-gray-300" : "text-gray-700"
                        }`}
                      >
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    records.map((row, idx) => {
                      const assetId = getAssetId(row);
                      const isSel = selectedIds.has(String(assetId));
                      const isScr = scrapIds.has(String(assetId));

                      return (
                        <tr
                          key={assetId || idx}
                          className={`transition-colors duration-200 ${rowClass(idx)}`}
                        >
                          {columns.map((c) => {
                            if (c.key === "sl") {
                              return (
                                <td
                                  key={c.key}
                                  className={
                                    mode !== "Land"
                                      ? "px-3 py-2 text-center border-r border-white/20"
                                      : `px-3 py-2 text-center ${
                                          isDark
                                            ? "border-r border-gray-700"
                                            : "border-r border-gray-200"
                                        }`
                                  }
                                >
                                  {idx + 1}
                                </td>
                              );
                            }

                            if (c.key === "update") {
                              return (
                                <td
                                  key={c.key}
                                  className={
                                    mode !== "Land"
                                      ? "px-3 py-2 text-center border-r border-white/20"
                                      : "px-3 py-2 text-center"
                                  }
                                >
                                  <button
                                    onClick={() => handleUpdate(row)}
                                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-md text-sm font-semibold ${
                                      mode !== "Land"
                                        ? "bg-white/20 hover:bg-white/30 text-white"
                                        : `${buttonClass} ${hoverClass} text-white`
                                    }`}
                                  >
                                    <FaEdit />
                                    Update
                                  </button>
                                </td>
                              );
                            }

                            if (c.key === "invoiceDoc" && mode !== "Land") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-3 py-2 text-center border-r border-white/20"
                                >
                                  <button
                                    onClick={() => openDocs("Invoice Document", row)}
                                    className="underline text-sm"
                                  >
                                    View
                                  </button>
                                </td>
                              );
                            }

                            if (c.key === "select" && mode !== "Land") {
                              return (
                                <td
                                  key={c.key}
                                  className="px-3 py-2 text-center border-r border-white/20"
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSel}
                                    onChange={() => toggleSelected(String(assetId))}
                                    className="w-4 h-4"
                                  />
                                </td>
                              );
                            }

                            if (c.key === "scrap" && mode !== "Land") {
                              return (
                                <td key={c.key} className="px-3 py-2 text-center">
                                  <input
                                    type="checkbox"
                                    checked={isScr}
                                    onChange={() => toggleScrap(String(assetId))}
                                    className="w-4 h-4"
                                  />
                                </td>
                              );
                            }

                            if (c.key === "landDoc" && mode === "Land") {
                              return (
                                <td
                                  key={c.key}
                                  className={`px-3 py-2 text-center ${
                                    isDark
                                      ? "border-r border-gray-700"
                                      : "border-r border-gray-200"
                                  }`}
                                >
                                  <button
                                    onClick={() => openDocs("Land Document", row)}
                                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                                  >
                                    Land Document
                                  </button>
                                </td>
                              );
                            }

                            if (c.key === "taxDoc" && mode === "Land") {
                              return (
                                <td key={c.key} className="px-3 py-2 text-center">
                                  <button
                                    onClick={() => openDocs("Tax Document", row)}
                                    className="text-blue-600 hover:text-blue-800 underline text-sm"
                                  >
                                    Tax Document
                                  </button>
                                </td>
                              );
                            }

                            const value = getRowValue(row, c.key);
                            const borderCls =
                              mode !== "Land"
                                ? "border-r border-white/20"
                                : isDark
                                ? "border-r border-gray-700"
                                : "border-r border-gray-200";

                            return (
                              <td
                                key={c.key}
                                className={`px-3 py-2 text-center ${
                                  c.key !== columns[columns.length - 1].key ? borderCls : ""
                                }`}
                              >
                                {value}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {mode !== "Land" && records.length > 0 && (
              <div className="flex justify-end gap-3 p-4">
                <button
                  onClick={handleRemoveSelected}
                  className={`px-6 py-2 rounded-lg font-semibold shadow ${buttonClass} ${hoverClass} text-white`}
                >
                  Remove
                </button>
                <button
                  onClick={handleConfirmScrap}
                  className={`px-6 py-2 rounded-lg font-semibold shadow ${buttonClass} ${hoverClass} text-white`}
                >
                  Confirm
                </button>
              </div>
            )}
          </div>

          {docOpen && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
              <div
                className={`w-full max-w-3xl rounded-2xl shadow-xl border p-5 ${
                  isDark
                    ? "bg-gray-900 border-gray-700 text-white"
                    : "bg-white border-gray-200 text-black"
                }`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="font-semibold text-lg">{docTitle}</div>
                  <button onClick={closeDocs} className="px-3 py-1 rounded-lg border">
                    Close
                  </button>
                </div>

                {docLoading ? (
                  <div className="py-10 text-center">Loading documents...</div>
                ) : docRows.length === 0 ? (
                  <div className="py-10 text-center">No documents</div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className={isDark ? "bg-gray-800" : "bg-gray-100"}>
                          <th className="text-left px-3 py-2">#</th>
                          <th className="text-left px-3 py-2">Doc Name</th>
                          <th className="text-left px-3 py-2">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {docRows.map((d, i) => {
                          const filename =
                            d.fld_docname ||
                            d.fld_DocName ||
                            d.fld_filename ||
                            d.filename ||
                            "";
                          return (
                            <tr key={i}>
                              <td className="px-3 py-2">{i + 1}</td>
                              <td className="px-3 py-2">{filename || JSON.stringify(d)}</td>
                              <td className="px-3 py-2">
                                {filename ? (
                                  <a
                                    className="text-blue-600 underline"
                                    href={`../Asset/Uploadfiles/${filename}`}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    View
                                  </a>
                                ) : (
                                  <span className="text-gray-500">N/A</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AssetLayoutWrapper>
  );
};

export default AssetMasterListing;